import React, { ChangeEvent, HTMLProps, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import AdvancedSearch from '@/assets/icons/AdvancedSearch';
import CloseXs from '@/assets/icons/CloseXs';
import Loader from '@/assets/icons/Loader';
import Search from '@/assets/icons/Search';
import { useBamAuth, useHasPermission } from '@/auth';
import { SearchType } from '@/components/search/types';
import { getClientNameFromSearchType, getSearchedHistoryIcon } from '@/components/search/v1/utils/helpers';
import { ChipButton } from '@/components/UI/ChipButton/ChipButton';
import { useCombineRefs } from '@/hooks/useCombineRefs';

import styles from './SearchInput.module.css';

interface SearchInputProps extends HTMLProps<HTMLInputElement> {
  loading?: boolean;
  hideSearchIcon?: boolean;
  value: string;
  isWithinAdvancedSearch?: boolean;
  onChangeCallback: (query: string) => void;
  handleOpenAdvancedSearchDialog?: () => void;
  onSearchIconClick?: () => void;
  onClearAll: () => void;
  onRemoveChip: () => void;
  error?: boolean;
  searchTypeSelected: SearchType;
  inputChipIsActive: boolean;
}

function SearchInput({
  error,
  searchTypeSelected,
  inputChipIsActive,
  loading,
  onChangeCallback,
  hideSearchIcon = false,
  isWithinAdvancedSearch,
  onSearchIconClick,
  handleOpenAdvancedSearchDialog,
  onClearAll,
  onRemoveChip,
  value,
  ref,
  ...restProps
}: SearchInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const { isLoggedIn } = useBamAuth();
  const isAllowedToView = useHasPermission('mwd.view');
  const [showChipButton, setShowChipButton] = useState<boolean>(true);
  const inputElemRef = useRef<HTMLInputElement>(null);
  const inputRef = useCombineRefs(ref ?? null, inputElemRef);
  const queryValue = inputElemRef?.current?.value;

  const internalCallback = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChangeCallback(e.target.value);
  }, []);

  const clearQueryCallback = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (inputElemRef.current) {
        onClearAll();
      }
    },
    [onClearAll],
  );

  const handleCloseChipButton = () => {
    onRemoveChip?.();
  };

  const handleAdvancedSearchClick = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (handleOpenAdvancedSearchDialog) {
      handleOpenAdvancedSearchDialog();
    }
  }, []);

  useEffect(() => {
    setShowChipButton(inputChipIsActive);
  }, [inputChipIsActive]);

  return (
    <div className={styles.searchInputContainer}>
      <input
        id='search-input'
        data-testid='searchInputTestId'
        tabIndex={0}
        ref={inputRef}
        type='text'
        autoComplete='off'
        value={value}
        style={error ? { border: `2px solid var(--tractie-color-content-error)`, boxShadow: 'none' } : undefined}
        disabled={!isAllowedToView}
        className={clsx(styles.searchInput, styles[`${searchTypeSelected}`], hideSearchIcon && styles.noSearchIcon)}
        onChange={internalCallback}
        title={!isLoggedIn ? 'Log eerst in om te kunnen zoeken' : ''}
        {...restProps}
      />
      <div className={clsx(styles.inputIconContainerStyle, hideSearchIcon && styles.noSearchIcon)}>
        {!hideSearchIcon && (
          <>
            {loading ? (
              <div className={styles.loadingIcon}>
                <Loader fill='var(--tractie-color-content-emphasis)' aria-hidden='true' />
              </div>
            ) : (
              <div onClick={onSearchIconClick} style={{ cursor: 'pointer', marginTop: '3px' }}>
                <Search fill='var(--tractie-color-content-emphasis)' data-testid='search-icon' aria-hidden='true' />
              </div>
            )}
          </>
        )}
        {showChipButton && searchTypeSelected !== 'empty' && (
          <div style={{ marginLeft: '8px' }}>
            <ChipButton
              size='small'
              transparentHover
              handleOnClose={handleCloseChipButton}
              label={getClientNameFromSearchType(searchTypeSelected)}
              icon={getSearchedHistoryIcon(searchTypeSelected)}
            />
          </div>
        )}
      </div>
      {handleOpenAdvancedSearchDialog && (
        <div
          data-testid='advancedSearchButton'
          className={`${styles.iconContainer} ${styles.setRightIconContainer}`}
          onMouseDown={handleAdvancedSearchClick}
          style={{ zIndex: 10 }}
        >
          <AdvancedSearch fill='var(--tractie-color-content-emphasis)' aria-hidden='true' />
        </div>
      )}
      {queryValue && (
        <div
          className={`${styles.iconContainer} ${isWithinAdvancedSearch ? `${styles.setRightIconContainer}` : `${styles.closeIconContainer}`}`}
          onMouseDown={clearQueryCallback}
          style={{ zIndex: 10 }}
        >
          <CloseXs fill='var(--tractie-color-content-emphasis)' data-testid='clear-input' aria-hidden='true' />
        </div>
      )}
    </div>
  );
}
export default SearchInput;
